/**
 * Open editors, as every layout shows them: the screen's icon with a pencil
 * (edit) or plus (add) mark, the record's title, the screen's name, an
 * "unsaved" dot, and × to close (which asks Save / Discard when dirty). The
 * list (a panel / menu) and the tray (a strip of chips) are two densities of
 * the same entry.
 */
import { AlertTriangle, Pencil, Plus, X } from 'lucide-react'
import type { CSSProperties } from 'react'
import { tw } from '../../../testHooks'
import { MINIMISE_TARGET_ATTR } from '../minimiseFly'
import { NavIcon } from '../navIcons'
import { S, iconButton } from '../tokens'
import type { EditorSession, ShellNavModel } from '../useShellNav'

export function editorTitle(s: EditorSession): string {
  return s.title || (s.mode === 'add' ? 'New record' : 'Record')
}

function EditorMark({ session, size = 30, model }: { session: EditorSession; size?: number; model: ShellNavModel }) {
  const item = model.itemFor(session.objName)
  return (
    <span aria-hidden style={{
      position: 'relative', width: size, height: size, flexShrink: 0, borderRadius: 8,
      background: S.accentWeak, color: S.accent,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <NavIcon node={item || { obj_type: 'T' }} size={Math.round(size * 0.55)} />
      <span style={{
        position: 'absolute', right: -4, bottom: -4, width: 16, height: 16, borderRadius: '50%',
        background: S.accent, color: '#fff', border: `2px solid ${S.panel}`,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {session.mode === 'add' ? <Plus size={9} strokeWidth={3} /> : <Pencil size={8} strokeWidth={3} />}
      </span>
    </span>
  )
}

function UnsavedDot() {
  return <span role="img" aria-label="Unsaved changes" title="Unsaved changes"
    style={{ width: 8, height: 8, borderRadius: '50%', background: S.unsaved, flexShrink: 0 }} />
}

export interface EditorsListProps {
  model: ShellNavModel
  /** Called after an entry is opened (close a menu / panel). */
  onPicked?: () => void
  emptyText?: string
  /** role="menuitem" rows inside a role="menu" container. */
  asMenu?: boolean
  style?: CSSProperties
}

/** The vertical list (a tab, a panel, a drop-down). */
export function EditorsList({ model, onPicked, emptyText, asMenu = false, style }: EditorsListProps) {
  if (!model.editors.length) {
    return (
      <p style={{ margin: 10, fontSize: 14, color: S.text2, lineHeight: 1.5, ...style }}>
        {emptyText || 'No open editors. Add or edit a record, then minimise it to keep it here.'}
      </p>
    )
  }
  return (
    <div role={asMenu ? undefined : 'list'} style={{ display: 'flex', flexDirection: 'column', gap: 2, ...style }}>
      {model.editors.map((s) => {
        const active = s.id === model.activeEditorId
        const screen = model.itemFor(s.objName)?.title || s.objName
        const title = editorTitle(s)
        return (
          <div key={s.id} role={asMenu ? 'none' : 'listitem'} style={{
            display: 'flex', alignItems: 'center', gap: 4, borderRadius: 8,
            background: active ? S.surface2 : 'transparent',
          }}>
            <button
              type="button"
              role={asMenu ? 'menuitem' : undefined}
              className="tw-sh-item"
              onClick={() => { model.restoreEditor(s.id); onPicked?.() }}
              aria-current={active ? 'true' : undefined}
              aria-label={`${s.mode === 'add' ? 'New' : 'Editing'} ${title}, ${screen}${s.dirty ? ', unsaved changes' : ''}`}
              {...tw('action', 'shell-editor', { obj: s.objName, mode: s.mode, txn: s.txnId, dirty: s.dirty })}
              style={{
                flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10,
                minHeight: 52, padding: '6px 10px', border: 'none', borderRadius: 8,
                background: 'transparent', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
                color: S.text,
              }}
            >
              <EditorMark session={s} model={model} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="tw-sh-clip" style={{ display: 'block', fontSize: 15, fontWeight: 500 }}>{title}</span>
                <span className="tw-sh-clip" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: S.text2 }}>
                  {screen}
                  {s.stale && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: S.warning }}>
                      <AlertTriangle size={12} aria-hidden /> old draft
                    </span>
                  )}
                </span>
              </span>
              {s.dirty && <UnsavedDot />}
            </button>
            <button
              type="button"
              className="tw-sh-btn"
              onClick={() => model.closeEditor(s.id)}
              aria-label={`Close ${title}`}
              title="Close"
              {...tw('action', 'shell-editor-close', { obj: s.objName, txn: s.txnId })}
              style={{ ...iconButton(34), marginRight: 4 }}
            >
              <X size={15} aria-hidden />
            </button>
          </div>
        )
      })}
    </div>
  )
}

/** The horizontal strip of chips (classic's bottom tray, rail's chip tray, flat's tray). */
export function EditorsTray({ model, label = 'Open editors', style }: {
  model: ShellNavModel; label?: string; style?: CSSProperties
}) {
  if (!model.editors.length) return null
  return (
    <div role="region" aria-label={label} {...tw('action', 'shell-editors-tray')} style={{
      flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, minHeight: 52,
      padding: '6px 96px 6px 14px', overflowX: 'auto',
      background: S.panel, borderTop: `1px solid ${S.border}`, ...style,
    }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6, paddingRight: 4, flexShrink: 0,
        fontSize: 13, fontWeight: 600, letterSpacing: '0.06em', color: S.text2, whiteSpace: 'nowrap',
        textTransform: 'uppercase',
      }}>
        <Pencil size={14} aria-hidden /> {label} · {model.editors.length}
      </span>
      {model.editors.map((s) => {
        const active = s.id === model.activeEditorId
        const screen = model.itemFor(s.objName)?.title || s.objName
        const title = editorTitle(s)
        return (
          <div key={s.id} {...{ [MINIMISE_TARGET_ATTR]: s.id }} style={{
            display: 'flex', alignItems: 'center', height: 40, flexShrink: 0, borderRadius: 9,
            border: `1px solid ${active ? S.accent : S.border}`, background: active ? S.accentWeak : S.bg,
          }}>
            <button
              type="button"
              className="tw-sh-item"
              onClick={() => model.restoreEditor(s.id)}
              aria-current={active ? 'true' : undefined}
              aria-label={`${s.mode === 'add' ? 'New' : 'Editing'} ${title}, ${screen}${s.dirty ? ', unsaved changes' : ''}`}
              {...tw('action', 'shell-editor', { obj: s.objName, mode: s.mode, txn: s.txnId, dirty: s.dirty })}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, height: 38, padding: '0 6px 0 10px',
                border: 'none', borderRadius: 9, background: 'transparent', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 14, fontWeight: 500, color: S.text, whiteSpace: 'nowrap',
              }}
            >
              <span aria-hidden style={{ display: 'inline-flex', color: S.accent }}>
                {s.mode === 'add' ? <Plus size={15} /> : <Pencil size={14} />}
              </span>
              {title}
              <span style={{ fontSize: 13, color: S.text2 }}>{screen}</span>
              {s.dirty && <UnsavedDot />}
            </button>
            <button
              type="button"
              className="tw-sh-btn"
              onClick={() => model.closeEditor(s.id)}
              aria-label={`Close ${title}`}
              {...tw('action', 'shell-editor-close', { obj: s.objName, txn: s.txnId })}
              style={{ ...iconButton(30), marginRight: 4 }}
            >
              <X size={14} aria-hidden />
            </button>
          </div>
        )
      })}
    </div>
  )
}

export { EditorMark, UnsavedDot }
